import React, { createContext, useState, useEffect, useRef, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWrapper } from '../../shared/api/fetchWrapper';
import { AuthContext } from './AuthContext';

export const WidgetContext = createContext();

export const WidgetProvider = ({ children }) => {
  const [memberWidget, setMemberWidget] = useState([]);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [widgetData, setWidgetData] = useState([]);
  const [widgetDataList, setWidgetDataList] = useState([]);
  const [widgetParams, setWidgetParams] = useState({});
  const [realTimeData, setRealTimeData] = useState({});
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [pieChartData, setPieChartData] = useState([]);
  const [tableData, setTableData] = useState({});
  const [broadcastData, setBroadcastData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const navigate = useNavigate();
  const fetchT001LPProcessId = process.env.REACT_APP_FETCH_T001LP;
  const getPieChartDataProcessId = process.env.REACT_APP_GET_PIE_CHART_DATA;
  const getTableDataProcessId = process.env.REACT_APP_GET_TABLE_DATA;
  // Auth context'ten token ve kimlik doğrulama durumunu al
  const { token, isAuthenticated } = useContext(AuthContext);

  // Widget'ların WebSocket bağlantılarını izlemek için
  
  const [defaultLayoutParams, setDefaultLayoutParams] = useState({
    x: 0,
    y: 0,
    w: 4,
    h: 5,

  });

  // Önbellek için yeni state'ler
  const [widgetCache, setWidgetCache] = useState({});
  const [cacheTimestamp, setCacheTimestamp] = useState({});
  const CACHE_DURATION = 5 * 60 * 1000; // 5 dakika

  // Önbellek kontrolü için yardımcı fonksiyon
  const isCacheValid = useCallback((widgetId) => {
    const timestamp = cacheTimestamp[widgetId];
    if (!timestamp) return false;
    return Date.now() - timestamp < CACHE_DURATION;
  }, [cacheTimestamp]);

  const initializeDashboard = useCallback(async () => {
    if (isInitialized) return;
    const currentToken = token || localStorage.getItem('token');
    if (!currentToken || !isAuthenticated) return;

    setIsInitialized(true);
    setIsLoading(true);
    try {
      await loadAllWidgetData(currentToken);
      getWidgetData();
    } finally {
      setIsLoading(false);
    }
  }, [token, isAuthenticated, isInitialized]);

  // Tüm widget verilerini yükleyen fonksiyon
  const loadAllWidgetData = async (manualToken = null) => {
    const currentToken = manualToken || token || localStorage.getItem('token');
    
    if (!currentToken) {
      console.warn("Token bulunamadı, widget verileri alınamıyor");
      return;
    }
    
    try {
      await getMemberWidget(currentToken);
      await getMemberWidgetData(currentToken);
    } catch (error) {
      console.error("Widget verileri yüklenirken hata:", error);
    }
  };
  const getPieChartData = useCallback(async (widgetId) => {
    const cacheKey = `pie-${widgetId || 'default'}`;
    
    // Önbellekte geçerli veri varsa onu kullan
    if (widgetCache[cacheKey] && isCacheValid(cacheKey)) {
      return widgetCache[cacheKey];
    }

    try {
      if (widgetId) {
        const widgetInfo = widgetData.find(w => w.ID.toString() === widgetId.toString());
        
        if (!widgetInfo || !widgetInfo.BAEND) {
          return null;
        }
        
        const response = await fetchWrapper(widgetInfo.BAEND, {
          token: localStorage.getItem('token'),
          process: widgetInfo.BAEND,
          ID: widgetId
        });
        
        if (!response || !response.data || !Array.isArray(response.data)) {
          return null;
        }
        
        const formattedData = {
          CHARTHEADER: response.CHARTHEADER || '',
          data: response.data.map(item => {
            const keys = Object.keys(item);
            const firstKey = keys[0];
            const secondKey = keys[1];
            
            // Güvenli değer alma
            const name = firstKey ? String(item[firstKey] || '') : '';
            const value = secondKey ? Number(item[secondKey]) || 0 : 0;
            
            return { name, value };
          })
        };
        
        // Önbelleğe kaydet
        setWidgetCache(prev => ({
          ...prev,
          [cacheKey]: formattedData
        }));
        
        setCacheTimestamp(prev => ({
          ...prev,
          [cacheKey]: Date.now()
        }));
        
        return formattedData;
      } else {
        const response = await fetchWrapper(getPieChartDataProcessId, {
          token: localStorage.getItem('token'),
          process: getPieChartDataProcessId,
        });
      
        if (!response || !response.data || !Array.isArray(response.data)) {
          return null;
        }
      
        const formattedData = {
          CHARTHEADER: response.CHARTHEADER || '',
          data: response.data.map(item => {
            const keys = Object.keys(item);
            const firstKey = keys[0];
            const secondKey = keys[1];
            
            // Güvenli değer alma
            const name = firstKey ? String(item[firstKey] || '') : '';
            const value = secondKey ? Number(item[secondKey]) || 0 : 0;
            
            return { name, value };
          })
        };
      
        // Önbelleğe kaydet
        setWidgetCache(prev => ({
          ...prev,
          [cacheKey]: formattedData
        }));
        
        setCacheTimestamp(prev => ({
          ...prev,
          [cacheKey]: Date.now()
        }));
        
        return formattedData;
      }
    } catch (error) {
      console.error("PieChartData fetch error:", error);
      return null;
    }
  }, [widgetData, isCacheValid]);
  const getTableData = async (widgetId, forceRefresh = false) => {
    if (!widgetId) {
      console.warn("Widget ID sağlanmadı, tablo verisi çekilemiyor");
      return null;
    }


    // Eğer bu widgetId için daha önce veri çekildiyse ve veri varsa tekrar çekme
    // forceRefresh true ise önbelleği atla
    if (!forceRefresh && tableData[widgetId] && Object.keys(tableData[widgetId]).length > 0) {
      return tableData[widgetId];
    }

    if (forceRefresh) {
    }

    try {
      // Önce widget bilgilerini al
      const widgetInfo = widgetData.find(w => w.ID.toString() === widgetId.toString());
      
      if (!widgetInfo) {
        return null;
      }
      
      // Widget'ın BAEND değerini al
      const baendValue = widgetInfo.BAEND;
      
      if (!baendValue) {
        return null;
      }
      
      const response = await fetchWrapper(baendValue, {
        token: localStorage.getItem('token'),
        process: baendValue,
        ID: widgetId
      });
      
      
      if (!response || !response.data) {
        console.error(`[getTableData] Geçersiz veya boş response: ${widgetId}`);
        setTableData(prevData => ({
          ...prevData,
          [widgetId]: null
        }));
        return null;
      }
      
      // Eğer response.data.rawData varsa, bu yapıyı kullan
      // Örnek: { rawData: { title: "GELECEK DÖNEM SİPARİŞLERİ", data: [...], columns: {...} } }
      let processedData = response;
      if (response.data && response.data.rawData) {
        processedData = response.data.rawData;
      }
      
      
      // Her widget ID'si için ayrı veri saklama
      setTableData(prevData => ({
        ...prevData,
        [widgetId]: processedData
      }));
      
      return processedData;
    } catch (error) {
      setTableData(prevData => ({
        ...prevData,
        [widgetId]: null
      }));
      return null;
    }
  };  
  const getBroadcastData = useCallback(async (widgetId) => {
    if (!widgetId) return null;

    // Önbellekte geçerli veri varsa onu kullan
    if (widgetCache[widgetId] && isCacheValid(widgetId)) {
      return widgetCache[widgetId];
    }

    try {
      const widgetInfo = widgetData.find(w => w.ID.toString() === widgetId.toString());
      
      if (!widgetInfo || !widgetInfo.BAEND) {
        return null;
      }
      
      const response = await fetchWrapper(widgetInfo.BAEND, {
        token: localStorage.getItem('token'),
        process: widgetInfo.BAEND,
        ID: widgetId
      });

      if (!response || !response.data) {
        return null;
      }

      const processedData = response.data.rawData || response;
      
      // Önbelleğe kaydet
      setWidgetCache(prev => ({
        ...prev,
        [widgetId]: processedData
      }));
      
      setCacheTimestamp(prev => ({
        ...prev,
        [widgetId]: Date.now()
      }));
      
      return processedData;
    } catch (error) {
      console.error("BroadcastData fetch error:", error);
      return null;
    }
  }, [widgetData, isCacheValid]);
  const getDoubleLineData = useCallback(async (widgetId) => {
    if (!widgetId) return null;

    // Önbellekte geçerli veri varsa onu kullan
    if (widgetCache[widgetId] && isCacheValid(widgetId)) {
      return widgetCache[widgetId];
    }

    try {
      const widgetInfo = widgetData.find(w => w.ID.toString() === widgetId.toString());
      
      if (!widgetInfo || !widgetInfo.BAEND) {
        return null;
      }
      
      const response = await fetchWrapper(widgetInfo.BAEND, {
        token: localStorage.getItem('token'),
        process: widgetInfo.BAEND,
        ID: widgetId
      });

      if (!response || !response.data) {
        return null;
      }
      
      const processedData = response.data.rawData || response;
      
      // Önbelleğe kaydet
      setWidgetCache(prev => ({
        ...prev,
        [widgetId]: processedData
      }));
      
      setCacheTimestamp(prev => ({
        ...prev,
        [widgetId]: Date.now()
      }));
      
      return processedData;
    } catch (error) {
      console.error("DoubleLineData fetch error:", error);
      return null;
    }
  }, [widgetData, isCacheValid]);
  // Widget seçildiğinde parametre formunu oluştur
  useEffect(() => {
    if (selectedWidget) {
      prepareWidgetParamsForm(selectedWidget);
    } else {
      setWidgetParams({});
    }
  }, [selectedWidget]);

  // Seçilen widget için parametre formu oluştur
  const prepareWidgetParamsForm = (widget) => {
    if (!widget || !widget.INPAR) {
      setWidgetParams({});
      return;
    }

    try {
      // INPAR formatını analiz et - beyaz boşlukları ve gereksiz karakterleri temizle
      let cleanInpar = widget.INPAR
        .trim()
        .replace(/[\r\n\t]+/g, '') // Satır sonu, sekme karakterlerini temizle
        .replace(/,\s*}/g, '}')    // Son virgülü temizle (JSON hatasına neden olur)
        .replace(/,\s*]/g, ']');   // Son virgülü temizle (array için)
      
      let inpar;

      try {
        // Önce normal JSON parse denemesi
        inpar = JSON.parse(cleanInpar);
      } catch (initialError) {
        console.warn(`Normal JSON parse başarısız oldu, hata düzeltme deneniyor: ${initialError.message}`);
        
        // JSON format hatası varsa, bazı yaygın sorunları düzeltmeyi dene
        // Tırnak işareti düzeltmeleri
        cleanInpar = cleanInpar
          .replace(/(['"])?([a-zA-Z0-9_]+)(['"])?:/g, '"$2":') // Tırnak içine alınmamış property adlarını düzelt
          .replace(/'/g, '"'); // Tek tırnakları çift tırnağa dönüştür
        
        inpar = JSON.parse(cleanInpar);
      }
      
      // Parametreleri form için hazırla
      const params = {};
      
      // INPAR formatı farklı olabilir
      if (typeof inpar === 'object') {
        if (Array.isArray(inpar)) {
          // Array formatı - parametre adı olarak index kullan
          inpar.forEach((value, index) => {
            params[`param${index + 1}`] = value;
          });
        } else {
          // Nesne formatı - {"1":"POINT"} veya {"paramName": "value"}
          Object.keys(inpar).forEach(key => {
            const paramName = isNaN(key) ? key : inpar[key];
            params[paramName] = typeof inpar[key] === 'object' 
              ? JSON.stringify(inpar[key]) 
              : isNaN(key) ? inpar[key] : "";
          });
        }
      }
      
      setWidgetParams(params);
    } catch (error) {
      console.error('Widget parametreleri ayrıştırılırken hata:', error);
      console.error('Hatalı INPAR:', widget.INPAR);
      setWidgetParams({});
    }
  };

  const getMemberWidget = async (manualToken = null) => {
    const currentToken = manualToken || token || localStorage.getItem('token');
    
    if (!currentToken) {
      console.warn("Token bulunamadı, memberWidget verileri alınamıyor");
      return [];
    }
    
    const memberWidgetProcessId = process.env.REACT_APP_MEMBER_WIDGET_PROCESS;
    try {
      const response = await fetchWrapper(memberWidgetProcessId, { token: currentToken }, navigate);
      setMemberWidget(response.widgets || []);
      return response.widgets || [];
    } catch (error) {
      console.error('Widget listesi alınırken hata:', error);
      return [];
    }
  };
  const fetchT001LP = async (point) => {
    try {
      const response = await fetchWrapper(fetchT001LPProcessId, {
        token: localStorage.getItem('token'),
        process: fetchT001LPProcessId,
        POINT: point
      });


      // Response kontrolü
      if (!response) {
        console.error("T001LP error: Boş response");
        return null;
      }

      // Response'da T001P objesi varsa direkt onu dön
      if (response.T001P) {
        return response.T001P;
      }

      // Response'da result true ise ve T001P objesi varsa
      if (response.result === true && response.T001P) {
        return response.T001P;
      }

      console.error("T001LP error: Geçersiz veri formatı", response);
      return null;
    } catch (error) {
      console.error("T001LP fetch error:", error);
      return null;
    }
  };
  const getMemberWidgetData = async (manualToken = null) => {
    const currentToken = manualToken || token || localStorage.getItem('token');
    
    if (!currentToken) {
      console.warn("Token bulunamadı, widgetData verileri alınamıyor");
      return [];
    }
    
    const getMemberWidgetDataProcessId = process.env.REACT_APP_GET_MEMBER_WIDGET_DATA;
    const formData = new FormData();
    formData.append('token', currentToken);
    formData.append('process', getMemberWidgetDataProcessId);
    
    try {
      const response = await fetchWrapper(getMemberWidgetDataProcessId, formData, navigate);
      setWidgetData(response.member_widgets || []);
      return response.member_widgets || [];
    } catch (error) {
      console.error('Widget verisi alınırken hata:', error);
      return [];
    }
  };

  const getWidgetData = async (widgets) => {
    const currentToken = token || localStorage.getItem('token');
    if (!currentToken) {
      return;
    }
    
    if (!widgets || !Array.isArray(widgets)) return;
    
    try {
      const promises = widgets.map(async (widget) => {
        if (!widget.BAEND) return null;
        
        const body = {
          token: currentToken,
        };
        return await fetchWrapper(widget.BAEND, body, navigate);
      });
      
      const results = await Promise.all(promises.filter(Boolean));
      setWidgetDataList(results);
    } catch (error) {
      console.error('Widget verileri alınırken hata:', error);
    }
  };

  // Parametre değerini güncelle
  const updateWidgetParam = (paramName, value) => {
    setWidgetParams(prev => ({
      ...prev,
      [paramName]: value
    }));
  };

  // Widget ekleme
  const addWidget = async (selectedWidget, parameters = {}) => {
    const currentToken = token || localStorage.getItem('token');
    if (!currentToken) {
      console.warn("Token bulunamadı, widget eklenemiyor");
      return;
    }
    
    const formData = new FormData();
    formData.append('token', currentToken);
    formData.append('process', process.env.REACT_APP_ADD_WIDGET);
    formData.append('WID', selectedWidget.WID);
    formData.append('layoutParams', JSON.stringify(defaultLayoutParams));
    // Parametre değerlerini INPAR formatına dönüştür
    const inparData = prepareInparData(selectedWidget, parameters);
    formData.append('INPAR', JSON.stringify(inparData));

    for (let index = 0; index < formData.length; index++) {
      const element = formData[index];
    }
    try {
      const response = await fetchWrapper(
        process.env.REACT_APP_ADD_WIDGET,
        formData,
        navigate
      );
      await loadAllWidgetData();
      
      return response;
    } catch (error) {
      console.error('Widget eklenirken hata:', error);
      throw error;
    }
  };

  // Parametre değerlerini INPAR formatına dönüştüren fonksiyon
  const prepareInparData = (widget, parameters) => {
    // Parametreler doğrudan gönderilmeli, hiçbir değişiklik yapmadan
    return parameters;
  };
  const saveLayout = async (allLayouts) => {
    try {
      const payload = [];
  
      Object.values(allLayouts).flat().forEach(item => {
        if (!item.i || typeof item.i !== 'string' || !item.i.includes('-')) {
          console.warn("⚠️ Geçersiz layout item bulundu:", item);
          return;
        }
  
        const layoutId = item.i.split('-')[1]; // örn: "pie-12" → "12"
        const layoutItem = {
          id: Number(layoutId), // backend artık id olarak alıyor
          layout: {
            x: item.x,
            y: item.y,
            w: item.w,
            h: item.h
          }
        };
        payload.push(layoutItem);
      });
      // Artık tüm payload'ı tek seferde backend'e gönderiyoruz
      const requestBody = {
        token: token || localStorage.getItem('token'),
        widgets: payload,
        process: process.env.REACT_APP_SAVE_LAYOUT
      };
      
      const response = await fetchWrapper(process.env.REACT_APP_SAVE_LAYOUT, requestBody);
      
      return response;
    } catch (err) {
      console.error("💥 Layout güncellenirken hata:", err);
      console.error("💥 Hata detayı:", err.message);
      console.error("💥 Hata stack:", err.stack);
      return { success: false, error: err.message };
    }
  };
  
  
  // Widget silme
  const removeWidget = async (WID) => {
    const currentToken = token || localStorage.getItem('token');
    
    if (!currentToken) {
      console.warn("Token bulunamadı, widget silinemiyor");
      return { success: false, error: "Kimlik doğrulama hatası" };
    }
    
    // Önce DOM'dan widgetı kaldırmak için mevcut verileri klonla
    const newWidgetData = widgetData.filter(widget => widget.ID.toString() !== WID.toString());
    
    // State'i hemen güncelle (optimistik update)
    setWidgetData(newWidgetData);
    
    try {
      // Widget için varsa süreç izleme aboneliğini sonlandır
      if (typeof unsubscribeFromRealTimeData === 'function') {
        unsubscribeFromRealTimeData(WID);
      }
      
      const removeWidgetProcessId = process.env.REACT_APP_REMOVE_WIDGET;
      const body = {
        token: currentToken,
        process: removeWidgetProcessId,
        WID: WID
      };
      // API isteğini yap
      const response = await fetchWrapper(removeWidgetProcessId, body, navigate);
      if (!response || response.error) {
        // Hata durumunda eski verileri geri yükle
        setWidgetData(widgetData);
        console.error("Widget silme işlemi başarısız:", response?.error || "Bilinmeyen hata");
        return { success: false, error: response?.error || "Widget silinirken bir hata oluştu" };
      }
      
      // Yanıt başarılıysa, pieChartData ve tableData'daki ilgili önbelleği temizle
      // (burada, widgetId varsa ilgili verileri siliyoruz)
      setTableData(prevData => {
        const newData = { ...prevData };
        delete newData[WID];
        return newData;
      });
      
      return { success: true, data: response };
    } catch (error) {
      // Hata durumunda eski verileri geri yükle
      setWidgetData(widgetData);
      console.error('Widget silinirken hata:', error);
      return { success: false, error: error.message || "Widget silinirken bir hata oluştu" };
    }
  };

 

  return (
    <WidgetContext.Provider value={{
        memberWidget,
        selectedWidget,
        setSelectedWidget,
      fetchT001LP,
      widgetData,
      widgetDataList,
      widgetParams,
      updateWidgetParam,
      defaultLayoutParams,
      addWidget,
      removeWidget,
      getMemberWidget,
        getMemberWidgetData,
      getWidgetData,
      isLoading,
      loadAllWidgetData,
      initializeDashboard,
      getPieChartData,
      pieChartData,
      tableData,
      getTableData,
      saveLayout,

      // WebSocket ile ilgili değerler ve fonksiyonlar
      realTimeData,
      isSocketConnected,
      broadcastData,
      getBroadcastData,
      getDoubleLineData,
    }}>
      {children}
    </WidgetContext.Provider>
  );
}; 